import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Mail, Building2, Users, Loader2, CheckCircle2, XCircle, DatabaseBackup, UserCog, UserPlus, Send, Trash2, Copy, MessageCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { formatarData, nomeDoPlano, STATUS_ASSINATURA } from "@/lib/planos";

function erro(err) {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : "Algo deu errado. Tente novamente.";
}

function Item({ ok, children }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="h-4 w-4 text-teal-600" /> : <XCircle className="h-4 w-4 text-slate-400" />}
      <span className={ok ? "text-slate-700 dark:text-slate-200" : "text-slate-500"}>{children}</span>
    </li>
  );
}

// Varios administradores: cada novo recebe por e-mail um link que abre o app,
// pede a senha e ja entra. Depois o app pede o 2FA.
function CartaoAdministradores() {
  const { user } = useAuth();
  const [lista, setLista] = useState([]);
  const [email, setEmail] = useState("");
  const [nome, setNome] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [convite, setConvite] = useState(null);

  const carregar = async () => {
    try {
      const r = await axios.get(`${API}/admin/administradores`);
      setLista(r.data);
    } catch (err) {
      toast.error(erro(err));
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const mostrarResultado = (r) => {
    if (r.data.link) {
      setConvite(r.data);
    } else {
      setConvite(null);
      toast.success(r.data.message);
    }
  };

  const adicionar = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      const r = await axios.post(`${API}/admin/administradores`, { email, name: nome });
      mostrarResultado(r);
      setEmail("");
      setNome("");
      await carregar();
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(false);
    }
  };

  const reenviar = async (a) => {
    if (!window.confirm(`Mandar um link de acesso novo para ${a.email}? O link anterior deixa de valer.`)) return;
    try {
      const r = await axios.post(`${API}/admin/administradores/${a.id}/convite`);
      mostrarResultado(r);
      await carregar();
    } catch (err) {
      toast.error(erro(err));
    }
  };

  const remover = async (a) => {
    if (!window.confirm(`Tirar o acesso de administrador de ${a.email}?`)) return;
    try {
      const r = await axios.post(`${API}/admin/administradores/${a.id}/remover`);
      toast.success(r.data.message);
      await carregar();
    } catch (err) {
      toast.error(erro(err));
    }
  };

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(convite.link);
      toast.success("Link copiado");
    } catch {
      toast.error("Não deu para copiar. Selecione o link e copie.");
    }
  };

  return (
    <Card data-testid="admins-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <UserCog className="h-5 w-5 text-teal-600" />
          <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Administradores</CardTitle>
        </div>
        <CardDescription>
          Quem pode abrir esta tela. Cada novo administrador recebe um link por e-mail para criar a senha e entrar. Depois, ativa a
          verificação em duas etapas.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={adicionar} className="flex flex-wrap items-end gap-2">
          <div className="grid gap-1">
            <Label htmlFor="adm-nome">Nome</Label>
            <Input id="adm-nome" value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Opcional" className="w-48" />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="adm-email">E-mail</Label>
            <Input id="adm-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="nome@email.com" className="w-64" data-testid="adm-email" />
          </div>
          <Button type="submit" disabled={ocupado} className="bg-teal-600 hover:bg-teal-700" data-testid="adm-adicionar">
            {ocupado ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}Adicionar
          </Button>
        </form>

        {convite && (
          <div className={`rounded-lg border p-3 text-sm ${convite.enviado ? "border-teal-200 bg-teal-50 text-teal-900" : "border-amber-200 bg-amber-50 text-amber-900"}`}
            data-testid="convite-resultado">
            <p>{convite.message}</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button type="button" size="sm" variant="outline" onClick={copiar}><Copy className="mr-1 h-4 w-4" />Copiar link</Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setConvite(null)}>Fechar</Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-slate-500">
              <tr>
                <th className="py-2 pr-4">Nome</th>
                <th className="py-2 pr-4">E-mail</th>
                <th className="py-2 pr-4">Situação</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-slate-800">
              {lista.map((a) => (
                <tr key={a.id}>
                  <td className="py-2 pr-4">{a.name}{a.id === user?.id && <span className="ml-1 text-xs text-slate-500">(você)</span>}</td>
                  <td className="py-2 pr-4">{a.email}</td>
                  <td className="py-2 pr-4">
                    {a.trocar_senha ? (
                      <Badge variant="outline" className="border-amber-300 text-amber-700">Aguardando 1º acesso</Badge>
                    ) : a.totp_ativo ? (
                      <Badge className="bg-teal-600">Ativo</Badge>
                    ) : (
                      <Badge variant="outline">Falta ativar o 2FA</Badge>
                    )}
                  </td>
                  <td className="whitespace-nowrap py-2 text-right">
                    {a.id !== user?.id && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => reenviar(a)} title="Mandar link de acesso de novo">
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remover(a)} title="Remover administrador">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminPage() {
  const [cfg, setCfg] = useState(null);
  const [assinantes, setAssinantes] = useState([]);
  const [chaveStripe, setChaveStripe] = useState("");
  const [chaveResend, setChaveResend] = useState("");
  const [remetente, setRemetente] = useState("");
  const [empresa, setEmpresa] = useState({ nome: "", documento: "", email_contato: "", cidade_uf: "" });
  const [suporte, setSuporte] = useState({ whatsapp: "", email: "" });
  const [ocupado, setOcupado] = useState(null);
  const [passos, setPassos] = useState([]);

  const carregar = async () => {
    try {
      const [c, a] = await Promise.all([axios.get(`${API}/admin/config`), axios.get(`${API}/admin/assinantes`)]);
      setCfg(c.data);
      setRemetente(c.data.email.remetente || "");
      setEmpresa(c.data.empresa);
      setSuporte({ whatsapp: c.data.suporte.whatsapp || "", email: c.data.suporte_email_proprio || "" });
      setAssinantes(a.data);
    } catch (err) {
      toast.error(erro(err));
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const salvar = async (campos, rotulo) => {
    setOcupado(rotulo);
    try {
      const r = await axios.put(`${API}/admin/config`, campos);
      setCfg(r.data);
      toast.success("Salvo");
      return true;
    } catch (err) {
      toast.error(erro(err));
      return false;
    } finally {
      setOcupado(null);
    }
  };

  const configurarStripe = async () => {
    setOcupado("configurar");
    setPassos([]);
    try {
      const r = await axios.post(`${API}/admin/stripe/configurar`, { origin_url: window.location.origin });
      setPassos(r.data.passos);
      toast.success(`Stripe configurado (modo ${r.data.modo === "live" ? "real" : "teste"})`);
      await carregar();
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(null);
    }
  };

  const testarEmail = async () => {
    setOcupado("teste-email");
    try {
      const r = await axios.post(`${API}/admin/email/testar`);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(erro(err));
    } finally {
      setOcupado(null);
    }
  };

  if (!cfg) return <div className="flex h-64 items-center justify-center"><div className="spinner" /></div>;
  const st = cfg.stripe;
  const ativos = assinantes.filter((u) => u.tem_acesso).length;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="admin-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "Manrope" }}>Administração</h1>
        <p className="text-slate-500 mt-1">Pagamentos, e-mail, dados da empresa e assinantes</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Stripe */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Stripe</CardTitle>
              {st.modo && <Badge className={st.modo === "live" ? "bg-teal-600" : "bg-amber-500"}>{st.modo === "live" ? "Modo real" : "Modo teste"}</Badge>}
            </div>
            <CardDescription>Chave em uso: {st.chave || "nenhuma"} {st.origem_chave === "ambiente" ? "(variável do servidor)" : st.origem_chave === "admin" ? "(salva aqui)" : ""}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="space-y-1">
              <Item ok={Object.keys(st.precos || {}).length === 3}>Produto e 3 preços (R$ 25 / R$ 50 / R$ 180)</Item>
              <Item ok={st.webhook_configurado}>Webhook {st.webhook_url ? `em ${st.webhook_url}` : ""}</Item>
              <Item ok={st.portal_configurado}>Portal do cliente (cancelar, trocar cartão e plano)</Item>
            </ul>
            {st.configurado_em && <p className="text-xs text-slate-500">Configurado em {new Date(st.configurado_em).toLocaleString("pt-BR")}</p>}
            <Button onClick={configurarStripe} disabled={ocupado !== null || !st.chave} className="bg-teal-600 hover:bg-teal-700" data-testid="admin-configurar-stripe">
              {ocupado === "configurar" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {st.configurado_em ? "Configurar de novo" : "Configurar Stripe agora"}
            </Button>
            {passos.length > 0 && (
              <ul className="space-y-1 rounded-lg bg-slate-50 p-3 text-sm dark:bg-slate-800">
                {passos.map((p) => <li key={p}>✓ {p}</li>)}
              </ul>
            )}
            <form
              className="space-y-2 border-t pt-4 dark:border-slate-800"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await salvar({ stripe_secret_key: chaveStripe }, "stripe")) setChaveStripe("");
              }}
            >
              <Label htmlFor="chave-stripe">Trocar a chave secreta (sk_test_... no preview, sk_live_... em produção)</Label>
              <div className="flex gap-2">
                <Input id="chave-stripe" type="password" value={chaveStripe} onChange={(e) => setChaveStripe(e.target.value)}
                  placeholder="sk_..." autoComplete="off" />
                <Button type="submit" variant="outline" disabled={ocupado !== null}>Salvar</Button>
              </div>
              <p className="text-xs text-slate-500">Deixe vazio e salve para voltar a usar a chave do servidor. Depois de trocar a chave, clique em Configurar.</p>
            </form>
          </CardContent>
        </Card>

        {/* E-mail */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>E-mail (Resend)</CardTitle>
              {cfg.email.configurado ? <Badge className="bg-teal-600">Ligado</Badge> : <Badge variant="outline">Desligado</Badge>}
            </div>
            <CardDescription>Usado no "esqueci a senha" e na confirmação de cadastro. Chave: {cfg.email.chave || "nenhuma"}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              className="space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                const campos = { email_remetente: remetente };
                if (chaveResend) campos.resend_api_key = chaveResend;
                if (await salvar(campos, "email")) setChaveResend("");
              }}
            >
              <div className="space-y-2">
                <Label htmlFor="chave-resend">Chave da API do Resend</Label>
                <Input id="chave-resend" type="password" value={chaveResend} onChange={(e) => setChaveResend(e.target.value)} placeholder="re_... (deixe vazio para manter)" autoComplete="off" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="remetente">Remetente</Label>
                <Input id="remetente" value={remetente} onChange={(e) => setRemetente(e.target.value)} placeholder="Aesthetica Pricing <nao-responda@appintuitivo.com.br>" />
                <p className="text-xs text-slate-500">O domínio do remetente precisa estar verificado no Resend.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="outline" disabled={ocupado !== null}>Salvar</Button>
                <Button type="button" onClick={testarEmail} disabled={ocupado !== null || !cfg.email.configurado} className="bg-teal-600 hover:bg-teal-700">
                  {ocupado === "teste-email" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enviar e-mail de teste
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Empresa */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Dados da empresa</CardTitle>
            </div>
            <CardDescription>Aparecem nos Termos de Uso, na Política de Privacidade e no rodapé da página de vendas.</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                salvar({
                  empresa_nome: empresa.nome,
                  empresa_documento: empresa.documento,
                  empresa_email_contato: empresa.email_contato,
                  empresa_cidade_uf: empresa.cidade_uf,
                }, "empresa");
              }}
            >
              <Input value={empresa.nome} onChange={(e) => setEmpresa({ ...empresa, nome: e.target.value })} placeholder="Razão social ou nome" />
              <Input value={empresa.documento} onChange={(e) => setEmpresa({ ...empresa, documento: e.target.value })} placeholder="CNPJ" />
              <Input type="email" value={empresa.email_contato} onChange={(e) => setEmpresa({ ...empresa, email_contato: e.target.value })} placeholder="E-mail de contato" />
              <Input value={empresa.cidade_uf} onChange={(e) => setEmpresa({ ...empresa, cidade_uf: e.target.value })} placeholder="Cidade/UF (foro)" />
              <Button type="submit" variant="outline" disabled={ocupado !== null} className="w-fit">Salvar</Button>
            </form>
          </CardContent>
        </Card>

        {/* Suporte */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Suporte</CardTitle>
            </div>
            <CardDescription>
              O WhatsApp aparece só para quem comprou (no menu do app). Quem ainda não comprou vê só o e-mail, no rodapé da página de vendas.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="grid gap-3"
              onSubmit={async (e) => {
                e.preventDefault();
                if (await salvar({ suporte_whatsapp: suporte.whatsapp, suporte_email: suporte.email }, "suporte")) {
                  await carregar();
                }
              }}
            >
              <div className="grid gap-1">
                <Label htmlFor="sup-whats">WhatsApp (DDD + número)</Label>
                <Input id="sup-whats" value={suporte.whatsapp} onChange={(e) => setSuporte({ ...suporte, whatsapp: e.target.value })}
                  placeholder="(11) 98888-7777" data-testid="suporte-whatsapp-input" />
                {cfg.suporte?.whatsapp_padrao && (
                  <p className="text-xs text-amber-700">Número provisório (fictício). Troque pelo número real.</p>
                )}
              </div>
              <div className="grid gap-1">
                <Label htmlFor="sup-email">E-mail de suporte</Label>
                <Input id="sup-email" type="email" value={suporte.email} onChange={(e) => setSuporte({ ...suporte, email: e.target.value })}
                  placeholder={empresa.email_contato ? `Vazio = ${empresa.email_contato}` : "suporte@seudominio.com.br"} />
              </div>
              <Button type="submit" variant="outline" disabled={ocupado !== null} className="w-fit">Salvar</Button>
            </form>
          </CardContent>
        </Card>

        {/* Backup */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <DatabaseBackup className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Backup automático</CardTitle>
            </div>
            <CardDescription>Uma cópia por cliente a cada 24 h, quando algo mudou. Guarda as 7 últimas.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Última execução: {cfg.ultimo_backup_em ? new Date(cfg.ultimo_backup_em).toLocaleString("pt-BR") : "ainda não rodou"}
            </p>
          </CardContent>
        </Card>
      </div>

      <CartaoAdministradores />

      {/* Assinantes */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-teal-600" />
            <CardTitle className="text-lg" style={{ fontFamily: "Manrope" }}>Assinantes</CardTitle>
          </div>
          <CardDescription>{assinantes.length} conta(s), {ativos} com acesso liberado</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {assinantes.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum assinante ainda.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="text-slate-500">
                <tr>
                  <th className="py-2 pr-4">Nome</th>
                  <th className="py-2 pr-4">E-mail</th>
                  <th className="py-2 pr-4">Plano</th>
                  <th className="py-2 pr-4">Situação</th>
                  <th className="py-2 pr-4">Vence/renova</th>
                  <th className="py-2">Desde</th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-slate-800">
                {assinantes.map((u) => (
                  <tr key={u.id}>
                    <td className="py-2 pr-4">{u.name}</td>
                    <td className="py-2 pr-4">{u.email}{!u.email_verificado && <span className="ml-1 text-xs text-amber-600">(não confirmado)</span>}</td>
                    <td className="py-2 pr-4">{u.status === "vitalicio" ? "Vitalício" : nomeDoPlano(u.plano) || "-"}</td>
                    <td className="py-2 pr-4">
                      <Badge className={u.tem_acesso ? "bg-teal-600" : "bg-slate-400"}>
                        {u.status === "vitalicio" ? "Vitalício" : STATUS_ASSINATURA[u.status] || u.status || "-"}
                      </Badge>
                      {u.cancelar_no_fim && <span className="ml-1 text-xs text-slate-500">cancela no fim</span>}
                    </td>
                    <td className="py-2 pr-4">{u.periodo_fim ? formatarData(u.periodo_fim) : "-"}</td>
                    <td className="py-2">{u.created_at ? new Date(u.created_at).toLocaleDateString("pt-BR") : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
